import { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";
import useVoiceRecorder from "@/lib/useVoiceRecorder";
import { LLM_REQUEST_TIMEOUT_MS } from "@/lib/constants";

const TABS = ["all", "expenses", "ask"];

export default function Memory() {
  const { t } = useLang();
  const [tab, setTab] = useState("all");
  const [captureOpen, setCaptureOpen] = useState(false);

  return (
    <div className="px-6 md:px-12 py-10 max-w-7xl mx-auto" data-testid="memory-page">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="label-cap">— {t("app.sidebar2.memory")}</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3" data-testid="memory-title">
            {t("app.memory.title")}
          </h1>
          <p className="font-display-italic text-lg mt-2 text-[var(--ink-soft)]">
            {t("app.memory.sub")}
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setCaptureOpen(true)}
          data-testid="memory-capture-open"
        >
          {t("app.memory.captureBtn")}
        </button>
      </header>

      <nav className="mt-10 flex border-b border-[var(--border)]" data-testid="memory-tabs">
        {TABS.map((id) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`memory-tab-${id}`}
            className={`px-6 py-3 text-[12px] tracking-monocle uppercase transition-colors duration-300 ${
              tab === id
                ? "text-[var(--ink)] border-b-2 border-[var(--oxblood)]"
                : "text-[var(--ink-soft)] border-b-2 border-transparent"
            }`}
          >
            {t(`app.memory.tab${id.charAt(0).toUpperCase() + id.slice(1)}`)}
          </button>
        ))}
      </nav>

      <div className="mt-10">
        {tab === "all" && <AllList />}
        {tab === "expenses" && <ExpensesDashboard />}
        {tab === "ask" && <AskPanel />}
      </div>

      {captureOpen && (
        <CaptureModal
          onClose={() => setCaptureOpen(false)}
          onSaved={() => setCaptureOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Tab: Tudo ────────────────────────────────────────────────────────────────

function AllList() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [docType, setDocType] = useState("");
  const [category, setCategory] = useState("");
  const [month, setMonth] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (docType) params.doc_type = docType;
      if (category) params.category = category;
      if (month) params.month = month;
      const { data } = await api.get("/memory", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [q, docType, category, month]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="memory-all">
      <div className="grid md:grid-cols-4 gap-3">
        <input
          className="input"
          placeholder={t("app.memory.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="memory-search-input"
        />
        <select
          className="input"
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          data-testid="memory-filter-type"
        >
          <option value="">{t("app.memory.filterAll")} · {t("app.memory.filterType")}</option>
          {Object.entries(t("app.memory.docTypes")).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          className="input"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="memory-filter-category"
        >
          <option value="">{t("app.memory.filterAll")} · {t("app.memory.filterCategory")}</option>
          {Object.entries(t("app.memory.categories")).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input
          type="month"
          className="input"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="memory-filter-month"
        />
      </div>

      {loading ? (
        <p className="mt-10 text-[var(--ink-faint)] label-cap">…</p>
      ) : items.length === 0 ? (
        <p
          className="mt-16 font-display-italic text-xl text-[var(--ink-soft)]"
          data-testid="memory-empty"
        >
          {t("app.memory.empty")}
        </p>
      ) : (
        <ul className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)]" data-testid="memory-list">
          {items.map((m) => (
            <MemoryCard key={m.id} memory={m} onOpen={() => setSelected(m.id)} />
          ))}
        </ul>
      )}

      {selected && (
        <MemoryDetail
          id={selected}
          onClose={() => setSelected(null)}
          onDeleted={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function MemoryCard({ memory, onOpen }) {
  const { t } = useLang();
  const types = t("app.memory.docTypes");
  const cats = t("app.memory.categories");
  return (
    <button
      onClick={onOpen}
      className="text-left bg-[var(--bg)] p-6 hover:bg-[var(--surface)] transition-colors duration-300"
      data-testid={`memory-card-${memory.id}`}
    >
      <p className="label-cap text-[var(--oxblood)]">
        — {types[memory.doc_type] || memory.doc_type}
      </p>
      <p className="font-display text-xl mt-2 leading-snug">{memory.title}</p>
      {memory.summary && (
        <p className="text-sm text-[var(--ink-soft)] mt-2 line-clamp-3">{memory.summary}</p>
      )}
      <div className="mt-4 flex items-center justify-between text-xs">
        {memory.amount != null ? (
          <span className="font-display text-lg" data-testid={`memory-amount-${memory.id}`}>
            {memory.amount.toFixed(2)} {memory.currency || ""}
          </span>
        ) : (
          <span className="label-cap text-[var(--ink-faint)]">
            {cats[memory.category] || memory.category || ""}
          </span>
        )}
        <span className="label-cap text-[var(--ink-faint)]">
          {(memory.captured_at || memory.created_at || "").slice(0, 10)}
        </span>
      </div>
    </button>
  );
}

function MemoryDetail({ id, onClose, onDeleted }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/memory/${id}`).then(({ data: d }) => {
      if (alive) {
        setData(d);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [id]);

  const onDelete = async () => {
    if (!window.confirm(t("app.memory.deleteConfirm"))) return;
    await api.delete(`/memory/${id}`);
    onDeleted();
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-[var(--ink)]/60 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="memory-detail-overlay"
    >
      <div
        className="bg-[var(--bg)] max-w-3xl w-full p-8 md:p-10 mt-10"
        onClick={(e) => e.stopPropagation()}
        data-testid="memory-detail"
      >
        {loading || !data ? (
          <p className="label-cap">…</p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="label-cap text-[var(--oxblood)]">
                  — {t("app.memory.docTypes")[data.doc_type] || data.doc_type}
                </p>
                <h2 className="font-display text-3xl mt-2">{data.title}</h2>
                {data.summary && (
                  <p className="font-display-italic text-lg text-[var(--ink-soft)] mt-3">
                    {data.summary}
                  </p>
                )}
              </div>
              <button onClick={onClose} className="btn-ghost" data-testid="memory-detail-close">
                ×
              </button>
            </div>

            <dl className="mt-8 grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
              {data.amount != null && (
                <Field label={t("app.memory.expensesThisMonth")}>
                  <span className="font-display text-xl" data-testid="memory-detail-amount">
                    {Number(data.amount).toFixed(2)} {data.currency || ""}
                  </span>
                </Field>
              )}
              {data.merchant && <Field label="—">{data.merchant}</Field>}
              {data.category && (
                <Field label={t("app.memory.filterCategory")}>
                  {t("app.memory.categories")[data.category] || data.category}
                </Field>
              )}
              {data.captured_at && (
                <Field label={t("app.memory.capturedAt")}>{data.captured_at.slice(0, 10)}</Field>
              )}
            </dl>

            {data.image_b64 && (
              <img
                src={`data:${data.image_mime || "image/jpeg"};base64,${data.image_b64}`}
                alt={data.title}
                className="mt-8 max-h-[60vh] w-auto mx-auto border border-[var(--border)]"
                data-testid="memory-detail-image"
              />
            )}

            {data.extracted_text && (
              <details className="mt-6">
                <summary className="label-cap cursor-pointer">extracted text</summary>
                <pre
                  className="mt-3 text-xs whitespace-pre-wrap text-[var(--ink-soft)]"
                  data-testid="memory-detail-text"
                >
                  {data.extracted_text}
                </pre>
              </details>
            )}

            {data.tags?.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {data.tags.map((tg) => (
                  <span
                    key={tg}
                    className="label-cap px-3 py-1 bg-[var(--surface)] text-[var(--ink-soft)]"
                  >
                    {tg}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-8 flex justify-between items-center">
              <span className="label-cap text-[var(--ink-faint)]">
                {t("app.memory.archivedAt")} · {(data.created_at || "").slice(0, 10)}
              </span>
              <button
                className="btn-ghost text-[var(--oxblood)]"
                onClick={onDelete}
                data-testid="memory-detail-delete"
              >
                {t("app.memory.deleteBtn")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <dt className="label-cap text-[var(--ink-faint)]">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}

// ─── Tab: Despesas ───────────────────────────────────────────────────────────

function ExpensesDashboard() {
  const { t } = useLang();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/memory/expenses/summary").then(({ data }) => {
      setSummary(data);
      setLoading(false);
    });
  }, []);

  const maxMonth = useMemo(
    () => Math.max(1, ...(summary?.by_month || []).map((m) => m.amount)),
    [summary],
  );
  const maxCat = useMemo(
    () => Math.max(1, ...(summary?.by_category_current || []).map((m) => m.amount)),
    [summary],
  );

  if (loading) return <p className="label-cap">…</p>;
  if (!summary) return null;
  const cats = t("app.memory.categories");
  const totals = summary.totals;
  const hasData =
    totals.current > 0 || totals.previous > 0 || (summary.by_month || []).some((m) => m.amount > 0);

  if (!hasData) {
    return (
      <p
        className="mt-10 font-display-italic text-xl text-[var(--ink-soft)]"
        data-testid="memory-expenses-empty"
      >
        {t("app.memory.expensesEmpty")}
      </p>
    );
  }

  return (
    <div className="space-y-12" data-testid="memory-expenses">
      <div className="grid md:grid-cols-2 gap-px bg-[var(--border)]">
        <Kpi
          label={t("app.memory.expensesThisMonth")}
          amount={totals.current}
          currency={summary.currency}
          dataTestId="expenses-current"
          accent
        />
        <Kpi
          label={t("app.memory.expensesPrevMonth")}
          amount={totals.previous}
          currency={summary.currency}
          delta={totals.delta_pct}
          dataTestId="expenses-previous"
        />
      </div>

      <section>
        <p className="label-cap">— {t("app.memory.expensesByCategory")}</p>
        <ul className="mt-6 space-y-3" data-testid="expenses-by-category">
          {summary.by_category_current.map((row) => (
            <li key={row.category} className="flex items-center gap-4">
              <span className="w-32 text-sm text-[var(--ink-soft)]">
                {cats[row.category] || row.category}
              </span>
              <div className="flex-1 h-2 bg-[var(--surface)]">
                <div
                  className="h-full bg-[var(--oxblood)]"
                  style={{ width: `${(row.amount / maxCat) * 100}%` }}
                />
              </div>
              <span className="w-24 text-right font-display text-base">
                {row.amount.toFixed(0)} {summary.currency}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <p className="label-cap">— {t("app.memory.expensesTrend")}</p>
        <div className="mt-6 flex items-end gap-3 h-40" data-testid="expenses-trend">
          {summary.by_month.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full">
              <div
                className="w-full bg-[var(--ink)]"
                style={{ height: `${(m.amount / maxMonth) * 100}%`, minHeight: m.amount > 0 ? "2px" : "0" }}
              />
              <span className="label-cap mt-2 text-[var(--ink-faint)]">
                {m.month.slice(5)}/{m.month.slice(2, 4)}
              </span>
            </div>
          ))}
        </div>
      </section>

      {summary.recent?.length > 0 && (
        <section>
          <p className="label-cap">— {t("app.memory.recent")}</p>
          <ul className="mt-4 divide-y divide-[var(--border)]">
            {summary.recent.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between text-sm">
                <span className="truncate">{r.title}</span>
                <span className="font-display text-base ml-4">
                  {r.amount?.toFixed(2)} {r.currency}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Kpi({ label, amount, currency, delta, dataTestId, accent }) {
  return (
    <div
      className={`p-8 ${accent ? "bg-[var(--ink)] text-[var(--bg)]" : "bg-[var(--bg)]"}`}
      data-testid={dataTestId}
    >
      <p className={`label-cap ${accent ? "text-[var(--gold)]" : "text-[var(--oxblood)]"}`}>
        — {label}
      </p>
      <p className="font-display text-5xl mt-4">
        {amount.toFixed(2)}{" "}
        <span className="text-base align-top">{currency}</span>
      </p>
      {delta != null && (
        <p
          className={`mt-2 label-cap ${
            delta > 0
              ? accent
                ? "text-[var(--gold)]"
                : "text-[var(--oxblood)]"
              : "text-[var(--ink-faint)]"
          }`}
        >
          {delta > 0 ? "+" : ""}
          {delta}%
        </p>
      )}
    </div>
  );
}

// ─── Tab: Perguntar ──────────────────────────────────────────────────────────

function AskPanel() {
  const { t } = useLang();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onAsk = async () => {
    if (!question.trim() || loading) return;
    setLoading(true);
    setError("");
    setAnswer("");
    try {
      const { data } = await api.post(
        "/memory/ask",
        { question },
        { timeout: LLM_REQUEST_TIMEOUT_MS },
      );
      setAnswer(data.answer);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="memory-ask">
      <textarea
        rows={3}
        className="input w-full"
        placeholder={t("app.memory.askPlaceholder")}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        data-testid="memory-ask-input"
      />
      <button
        className="btn-primary mt-4"
        onClick={onAsk}
        disabled={loading}
        data-testid="memory-ask-submit"
      >
        {loading ? t("app.memory.asking") : t("app.memory.askBtn")}
      </button>
      {error && <p className="mt-4 text-sm text-[var(--error)]">{error}</p>}
      {answer && (
        <div
          className="mt-8 p-8 bg-[var(--surface)] border-l-2 border-[var(--oxblood)]"
          data-testid="memory-ask-answer"
        >
          <p className="font-display-italic text-lg whitespace-pre-wrap leading-relaxed">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Capture modal ───────────────────────────────────────────────────────────

function CaptureModal({ onClose, onSaved }) {
  const { t } = useLang();
  const [mode, setMode] = useState("photo");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const voice = useVoiceRecorder();

  const submit = async () => {
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      let response;
      if (mode === "text") {
        if (!text.trim()) {
          setError("…");
          setLoading(false);
          return;
        }
        response = await api.post(
          "/memory/capture",
          { text },
          { timeout: LLM_REQUEST_TIMEOUT_MS },
        );
      } else {
        const fd = new FormData();
        if (mode === "photo" && file) fd.append("file", file);
        else if (mode === "audio" && voice.blob) {
          fd.append("file", new File([voice.blob], "voice.webm", { type: "audio/webm" }));
        } else {
          setError("…");
          setLoading(false);
          return;
        }
        response = await api.post("/memory/capture", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: LLM_REQUEST_TIMEOUT_MS,
        });
      }
      onSaved(response.data.memory);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[var(--ink)]/60 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="capture-overlay"
    >
      <div
        className="bg-[var(--bg)] max-w-xl w-full p-8 md:p-10"
        onClick={(e) => e.stopPropagation()}
        data-testid="capture-modal"
      >
        <p className="label-cap">— Mordomo</p>
        <h2 className="font-display text-3xl mt-2">{t("app.memory.captureModalTitle")}</h2>
        <p className="font-display-italic text-base text-[var(--ink-soft)] mt-2">
          {t("app.memory.captureModalSub")}
        </p>

        <div className="mt-6 flex gap-px bg-[var(--border)]">
          {[
            ["photo", t("app.memory.capturePhoto")],
            ["audio", t("app.memory.captureAudio")],
            ["text", t("app.memory.captureText")],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              data-testid={`capture-mode-${id}`}
              className={`flex-1 py-3 text-[12px] tracking-monocle uppercase ${
                mode === id ? "bg-[var(--ink)] text-[var(--bg)]" : "bg-[var(--surface)] text-[var(--ink-soft)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {mode === "photo" && (
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
              data-testid="capture-photo-input"
            />
          )}
          {mode === "audio" && (
            <div className="flex items-center gap-4">
              {!voice.recording && !voice.blob && (
                <button onClick={voice.start} className="btn-outline" data-testid="capture-audio-start">
                  ●
                </button>
              )}
              {voice.recording && (
                <button onClick={voice.stop} className="btn-primary" data-testid="capture-audio-stop">
                  ■ {voice.seconds}s
                </button>
              )}
              {voice.blob && !voice.recording && (
                <>
                  <span className="label-cap text-[var(--ink-faint)]">{voice.seconds}s</span>
                  <button onClick={voice.reset} className="btn-ghost">×</button>
                </>
              )}
            </div>
          )}
          {mode === "text" && (
            <textarea
              rows={4}
              className="input w-full"
              placeholder={t("app.memory.captureTextPlaceholder")}
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="capture-text-input"
            />
          )}
        </div>

        {error && <p className="mt-4 text-sm text-[var(--error)]">{error}</p>}

        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost" data-testid="capture-cancel">
            ×
          </button>
          <button
            onClick={submit}
            disabled={loading}
            className="btn-primary"
            data-testid="capture-submit"
          >
            {loading ? t("app.memory.capturing") : t("app.memory.captureSubmit")}
          </button>
        </div>
      </div>
    </div>
  );
}
